import {
  Component,
  ElementRef,
  HostBinding,
  Inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  AiFeaturesService,
  LANGFUSE_SYSTEM_TAG,
  MaximizerAiService,
  PIIContext,
  PiiAnonymizationService,
  SYSTEM_PROMPT_ANONYMIZED_CONSIDERATIONS,
  SYSTEM_PROMPT_CHANGE_LANGUAGE,
} from '@maximizer/ai/data-access';
import {
  AddressBookService,
  ContextService,
  LocaleService,
  OpenAiService,
  SessionService,
} from '@maximizer/core/shared/data-access';
import {
  CDN_URL,
  Entity,
  FieldSet,
  Octopus,
  OpenAI,
  Session,
} from '@maximizer/core/shared/domain';
import { EmbeddedComponent } from '@maximizer/core/shared/ui/components/base';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import { ActionEventType, KeyArgs } from '@maximizer/core/shared/ui/config';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import {
  ResourceOrTranslatePipe,
  UI_PIPES,
} from '@maximizer/core/shared/ui/pipes';
import {
  TimelineFilter,
  TimelineService,
} from '@maximizer/core/timeline/data-access';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { KENDO_TABSTRIP } from '@progress/kendo-angular-layout';
import { finalize, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import {
  EventAction,
  EventJourney,
  MeetingPreparationSummaryEventTracker,
} from '../classes/event-tracker';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  MeetingPreparationSummaryHtmlBuilder,
  MeetingPreparationSummaryPrompt,
  Summary,
} from '../classes/meeting-preparation-summary';
import { getTimelineTexts } from '../classes/timeline-event';
import { AiModels } from '../classes/ai-models';
import { Helper } from '../classes/iqboost';
import { FeedbackComponent } from '../feedback/feedback.component';
import { DeAnonymizerPipe } from '../pipes/de-anonymizer.pipe';
import { SafeHtmlPipe } from '../pipes/safe-html.pipe';

const SUMMARY_PROMPT_NAME = 'meeting-preparation-summary';

@Component({
  selector: 'ai-meeting-preparation-summary',
  templateUrl: './meeting-preparation-summary.component.html',
  providers: [ResourceOrTranslatePipe, MeetingPreparationSummaryEventTracker],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FeedbackComponent,
    DeAnonymizerPipe,
    SafeHtmlPipe,
    TranslatePipe,
    KENDO_BUTTON,
    KENDO_DIALOG,
    KENDO_TABSTRIP,
    UI_KENDO,
    UI_LOADER,
    UI_PIPES,
  ],
})
export class MeetingPreparationSummaryComponent extends EmbeddedComponent {
  @HostBinding('class.ai-ng') croesusClass = true;

  args?: KeyArgs;
  loading = signal(false);
  isOpen = signal(false);
  summary = signal<Summary | null>(null);
  summaryText = signal('');
  entryName = signal('');
  entryType = signal('');
  entryContext = '';
  currentEntry?: Entity;
  sessionInfo?: Session;

  constructor(
    element: ElementRef,
    private readonly context: ContextService,
    private readonly session: SessionService,
    private readonly timeline: TimelineService,
    private readonly openAi: OpenAiService,
    private readonly piiAnonymization: PiiAnonymizationService,
    private readonly aiFeaturesService: AiFeaturesService,
    private readonly addressBook: AddressBookService,
    private readonly resourceOrTranslate: ResourceOrTranslatePipe,
    private readonly translate: TranslateService,
    private readonly locale: LocaleService,
    private readonly maximizerAi: MaximizerAiService,
    public readonly tracker: MeetingPreparationSummaryEventTracker,
    @Inject(CDN_URL) readonly cdn: string,
  ) {
    super(element);
  }

  override doAction(name: string, args: KeyArgs): void {
    if (name === ActionEventType.Show || name === ActionEventType.Initialize) {
      this.args = args;
      this.load();
      this.open();
    } else {
      super.doAction(name, args);
    }
  }

  open(): void {
    this.context.startDialogMode();
    this.isOpen.set(true);
    this.tracker.trackEvent(EventJourney.Summarization, EventAction.Started);
  }

  close(): void {
    this.clear();
    this.context.endDialogMode();
    this.isOpen.set(false);
    this.tracker.trackEvent(EventJourney.Summarization, EventAction.Closed);
  }

  clear(): void {
    this.entryContext = '';
    this.currentEntry = undefined;
    this.entryName.set('');
    this.entryType.set('');
    this.summary.set(null);
    this.summaryText.set('');
  }

  load(): void {
    this.clear();

    if (this.args?.key) {
      const key = this.args.key;

      this.loading.set(true);
      this.addSubscription(
        this.session.getInfo()
          .pipe(
            switchMap((sessionInfo) =>
              this.aiFeaturesService.processFiltersConfiguration(sessionInfo)
                .pipe(
                  map(() => sessionInfo),
                )
            ),
            switchMap((sessionInfo) =>
              this.addressBook.forReadWithMetadata(key)
                .pipe(
                  tap((entry) => {
                    this.sessionInfo = sessionInfo;
                    this.loadEntry(entry.value, entry.fields);
                  }),
                )
            ),
            switchMap(() => this.summarize()),
          )
          .subscribe(),
      );
    }
  }

  loadEntry(entry: Entity, fields: FieldSet): void {
    this.currentEntry = entry;

    const getLabel = (field: string): string => fields[field]?.name ?? '';

    const getValue = (field: string): string =>
      (entry[field] as Octopus.ValueAndDisplay<string>)?.DisplayValue ?? '';

    const formatField = (field: string): string => {
      const value = getValue(field);
      return value ? `${getLabel(field)}: "${value}"` : '';
    };

    this.entryName.set(getValue(Octopus.EntityFields.DisplayValue));
    this.entryType.set(getValue(Octopus.AbEntryFields.Type));
    this.entryContext = [
      formatField(Octopus.AbEntryFields.Type),
      formatField(Octopus.AbEntryFields.FullName),
      getValue(Octopus.AbEntryFields.Type) !== Octopus.AbEntryType.Individual
        ? formatField(Octopus.AbEntryFields.Parent)
        : '',
      formatField(Octopus.AbEntryFields.Status),
      formatField(Octopus.AbEntryFields.ImportantNote),
      formatField(Octopus.AbEntryFields.LastContactDate),
    ]
      .filter((line) => line.length > 0)
      .join('\n');
  }

  summarize(): Observable<Summary | null> {
    if (!this.args?.key) {
      return of(null);
    }

    const dateOffset = Octopus.getDateOffset(
      Octopus.DateOptionId.LASTSIXMONTHS,
    );
    this.loading.set(true);

    return forkJoin({
      events: this.timeline.forEntry(this.args.key, 'addressBook', {
        customDateQuery:
          typeof dateOffset === 'string'
            ? {
                $EQ: dateOffset,
              }
            : {
                $RANGE: dateOffset,
              },
      } as TimelineFilter),
      summaryPrompts: this.maximizerAi.lookupPrompts({
        project: 'MaximizerAI',
        filter: { name: [SUMMARY_PROMPT_NAME] },
      }),
      systemPrompts: this.maximizerAi.lookupPrompts({
        project: this.aiFeaturesService.isSLE() ? 'IqBoostSLE' : 'IqBoost',
        filter: {
          name: [
            SYSTEM_PROMPT_ANONYMIZED_CONSIDERATIONS,
            SYSTEM_PROMPT_CHANGE_LANGUAGE,
          ],
          tag: [LANGFUSE_SYSTEM_TAG],
        },
      }),
    }).pipe(
      switchMap(({ events, summaryPrompts, systemPrompts }) => {
        if (events.length) {
          const texts = getTimelineTexts(
            this.resourceOrTranslate,
            this.locale,
            events,
          );
          const promptClient = summaryPrompts.find(
            (prompt) => prompt.name === SUMMARY_PROMPT_NAME,
          );
          const languageTextPrompt = systemPrompts.find(
            (prompt) => prompt.name === SYSTEM_PROMPT_CHANGE_LANGUAGE,
          );
          // Lookup returns both system prompts; anonymization gates use, not fetch.
          const anonymizedConsiderationsPrompt = this.piiAnonymization.anonymized()
            ? systemPrompts.find(
                (prompt) =>
                  prompt.name === SYSTEM_PROMPT_ANONYMIZED_CONSIDERATIONS,
              )
            : undefined;
          const config = promptClient?.config as
            | OpenAI.RequestConfiguration
            | undefined;
          const contextMessages: OpenAI.GPTMessage[] = [{ role: 'user', content: this.entryContext }];
          const userMessages: OpenAI.GPTMessage[] = texts.map((content) => ({ role: 'user', content }));
          const systemMessages = [
            Helper.getLanguageSuggestedPrompt(languageTextPrompt),
            anonymizedConsiderationsPrompt?.prompt ?? '',
            promptClient?.prompt ?? MeetingPreparationSummaryPrompt,
          ];

          return this.piiAnonymization.anonymizeContextAndUser(
            { context: contextMessages, user: userMessages, system: [] },
            this.getPiiContext(),
          ).pipe(
            switchMap(({ context, user }) =>
              this.openAi.summarize<Summary>(
                context.map((message) => message.content),
                user.map((message) => message.content),
                systemMessages.map((message) => message).join('\n\n'),
                config?.model
                  ? config
                  : {
                      model: AiModels.gpt4o,
                      max_tokens: 700,
                      temperature: 0.1,
                      response_format: { type: 'json_object' },
                    },
              ),
            ),
          );
        }
        return of(null);
      }),
      tap((summary) => {
        this.setSummary(summary);
        this.tracker.trackEvent(
          EventJourney.Summarization,
          EventAction.Summarized,
        );
      }),
      finalize(() => this.loading.set(false)),
    );
  }

  private getPiiContext(): PIIContext {
    return {
      contacts: Helper.buildPIIContactRecords(
        this.currentEntry ? [this.currentEntry] : [],
        this.sessionInfo,
      ),
    };
  }

  private setSummary(summary: Summary | null): void {
    this.summary.set(summary);
    if (summary) {
      this.summaryText.set(
        new MeetingPreparationSummaryHtmlBuilder(
          summary,
          this.translate,
        )
          .clientOverview()
          .recentInteractions()
          .taskEvaluation()
          .agendaSuggestions()
          .actionableInsights()
          .build(),
      );
    } else {
      this.summaryText.set('');
    }
  }

  copyToClipboard(): void {
    if (navigator.clipboard) {
      this.tracker.trackEvent(EventJourney.Action, EventAction.Clicked, {
        name: 'copy-to-clipboard',
      });
      const html = this.piiAnonymization.deAnonymizeHtml(this.summaryText());
      const blob = new Blob([html], { type: 'text/html' });
      const clipboardItem = new ClipboardItem({ 'text/html': blob });
      navigator.clipboard.write([clipboardItem]);
    }
  }
}
